import { useId, useRef, useState } from 'react';

import usePwaInstall from '../../hooks/usePwaInstall';
import { PWA_COPY } from '../../utils/pwaInstall';
import PwaInstallInstructions from './PwaInstallInstructions';

/**
 * Compact mobile bottom promotion for PWA install / Add to Home Screen.
 * Non-modal: does not trap focus. Integrates at RootLayout shell level.
 */
export default function PwaInstallPromotion() {
  const {
    shouldShowPromotion,
    installPath,
    dismiss,
    promptInstall,
  } = usePwaInstall();

  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [installBusy, setInstallBusy] = useState(false);
  const howToBtnRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  if (!shouldShowPromotion && !instructionsOpen) {
    return null;
  }

  const handleInstall = async () => {
    if (installBusy) return;
    setInstallBusy(true);
    try {
      await promptInstall();
    } finally {
      setInstallBusy(false);
    }
  };

  const handleHowTo = () => {
    setInstructionsOpen(true);
  };

  const handleCloseInstructions = () => {
    setInstructionsOpen(false);
  };

  return (
    <>
      {shouldShowPromotion ? (
        <aside
          className="pwa-install-promotion"
          data-testid="pwa-install-promotion"
          data-install-path={installPath}
          aria-labelledby={titleId}
          aria-describedby={descId}
        >
          <div className="pwa-install-promotion__inner">
            <div className="pwa-install-promotion__text">
              <h2 id={titleId} className="pwa-install-promotion__title">
                {PWA_COPY.title}
              </h2>
              <p id={descId} className="pwa-install-promotion__body">
                {PWA_COPY.body}
              </p>
              {installPath === 'android-manual' ? (
                <p className="pwa-install-promotion__manual" data-testid="pwa-android-manual">
                  {PWA_COPY.androidManual}
                </p>
              ) : null}
            </div>

            <div className="pwa-install-promotion__actions">
              {installPath === 'chromium' ? (
                <button
                  type="button"
                  className="action-btn action-btn--accent pwa-install-promotion__primary"
                  data-testid="pwa-install-accept"
                  onClick={handleInstall}
                  disabled={installBusy}
                >
                  {PWA_COPY.install}
                </button>
              ) : null}

              {installPath === 'ios' ? (
                <button
                  ref={howToBtnRef}
                  type="button"
                  className="action-btn action-btn--accent pwa-install-promotion__primary"
                  data-testid="pwa-install-how-to"
                  onClick={handleHowTo}
                >
                  {PWA_COPY.howToInstall}
                </button>
              ) : null}

              <button
                type="button"
                className="action-btn pwa-install-promotion__dismiss"
                data-testid="pwa-install-dismiss"
                onClick={dismiss}
              >
                {PWA_COPY.notNow}
              </button>

              <button
                type="button"
                className="pwa-install-promotion__close"
                data-testid="pwa-install-close"
                aria-label={PWA_COPY.close}
                onClick={dismiss}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </div>
        </aside>
      ) : null}

      <PwaInstallInstructions
        open={instructionsOpen}
        onClose={handleCloseInstructions}
        returnFocusRef={howToBtnRef}
      />
    </>
  );
}
